import React from 'react';
import {
  TrendingUp,
  Target,
  Zap,
  CheckCircle2,
  AlertCircle,
  Clock,
  ArrowUpRight,
  CalendarCheck,
} from 'lucide-react';
import { formatNpr, formatNumber, formatPct } from './biTheme';

const formatVal = (val, metricType) => {
  if (metricType === 'gmv') return formatNpr(val);
  if (metricType === 'aov') return formatNpr(val);
  return formatNumber(val);
};

const BiBudgetTargetCard = ({
  title,
  periodName,
  daysLabel,
  metricType = 'gmv',
  data = {},
  color = '#8b5cf6',
}) => {
  const target = Number(data?.target || 0);
  const achieved = Number(data?.achieved || 0);
  const achievedPct = Number(data?.achievedPct || 0);
  const dailyRunRate = Number(data?.dailyRunRate || 0);
  const projectedTotal = Number(data?.projectedTotal || 0);
  const runRateAchievedPct = Number(data?.runRateAchievedPct || 0);
  const proRatedTarget = Number(data?.proRatedTarget || 0);
  const elapsedDays = Number(data?.elapsedDays || 1);
  const totalDays = Number(data?.totalDays || elapsedDays);
  const remainingDays = Number(data?.remainingDays || 0);

  // Till-date calculation
  const toDateAchievedPct = proRatedTarget > 0 ? (achieved / proRatedTarget) * 100 : 0;
  const toDateVariance = achieved - proRatedTarget;
  const isAheadOfToDate = toDateVariance >= 0;
  const varianceRatioPct = proRatedTarget > 0 ? (toDateVariance / proRatedTarget) * 100 : 0;

  // Time elapsed ratio for the pacing marker
  const timeElapsedPct = totalDays > 0 ? Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100)) : 0;
  const progressBarWidth = Math.min(100, Math.max(0, achievedPct));

  const isAhead = runRateAchievedPct >= 100;
  const isOnTrack = runRateAchievedPct >= 85 && runRateAchievedPct < 100;

  const statusBadge = isAhead ? (
    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
      <CheckCircle2 className="h-3 w-3" /> Ahead of Target
    </span>
  ) : isOnTrack ? (
    <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700">
      <Clock className="h-3 w-3" /> On Track
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
      <AlertCircle className="h-3 w-3" /> Behind Target
    </span>
  );

  return (
    <div className="relative flex flex-col justify-between overflow-hidden rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_6px_20px_rgba(15,23,42,0.04)] transition hover:shadow-md">
      {/* Top Header Accent */}
      <span
        className="absolute inset-x-0 top-0 h-1"
        style={{ background: `linear-gradient(90deg, ${color}, ${color}99)` }}
        aria-hidden
      />

      {/* Card Header */}
      <div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span
              className="flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold text-white shadow-sm"
              style={{ backgroundColor: color }}
            >
              {title}
            </span>
            <div>
              <h4 className="text-sm font-bold text-slate-900">{periodName}</h4>
              <p className="text-xs text-slate-400">{daysLabel || `Day ${elapsedDays} of ${totalDays}`}</p>
            </div>
          </div>
          {statusBadge}
        </div>

        {/* 1. TILL DATE (To-Date Pacing) Primary Section */}
        <div className="mt-3.5 rounded-xl border border-purple-100 bg-purple-50/30 p-3">
          <div className="flex items-center justify-between border-b border-purple-100/60 pb-1.5 text-[11px] font-bold text-purple-900 uppercase tracking-wider">
            <span className="flex items-center gap-1">
              <CalendarCheck className="h-3.5 w-3.5 text-purple-600" /> Target Till Date vs Achieved
            </span>
            <span className={`font-extrabold ${isAheadOfToDate ? 'text-emerald-700' : 'text-amber-700'}`}>
              {formatPct(toDateAchievedPct, 1)} To-Date
            </span>
          </div>

          <div className="mt-2 grid grid-cols-2 gap-3">
            <div>
              <span className="text-[10px] font-medium text-slate-500 uppercase">Target (Till Day {elapsedDays})</span>
              <p className="mt-0.5 text-sm font-bold text-slate-800 truncate" title={formatVal(proRatedTarget, metricType)}>
                {formatVal(proRatedTarget, metricType)}
              </p>
            </div>
            <div className="border-l border-purple-100/80 pl-3">
              <span className="text-[10px] font-medium text-slate-500 uppercase">Actual Achieved</span>
              <p className="mt-0.5 text-sm font-bold text-slate-900 truncate" title={formatVal(achieved, metricType)}>
                {formatVal(achieved, metricType)}
              </p>
            </div>
          </div>

          <div className="mt-2 flex items-center justify-between text-[11px]">
            <span className="text-slate-500">Till-Date Variance:</span>
            <span className={`font-bold tabular-nums ${isAheadOfToDate ? 'text-emerald-700' : 'text-rose-600'}`}>
              {isAheadOfToDate ? '+' : ''}{formatVal(toDateVariance, metricType)} ({isAheadOfToDate ? '+' : ''}{formatPct(varianceRatioPct, 1)} {isAheadOfToDate ? 'Surplus' : 'Deficit'})
            </span>
          </div>
        </div>

        {/* 2. Full Period Target vs Achieved Row */}
        <div className="mt-2.5 grid grid-cols-2 gap-3 rounded-xl bg-slate-50/70 p-2.5 text-xs">
          <div>
            <span className="flex items-center gap-1 text-[10px] font-medium text-slate-500 uppercase tracking-wider">
              <Target className="h-3 w-3 text-slate-400" /> Full {title} Target
            </span>
            <p className="mt-0.5 text-sm font-bold text-slate-900 truncate" title={formatVal(target, metricType)}>
              {formatVal(target, metricType)}
            </p>
          </div>

          <div className="border-l border-slate-200/80 pl-3">
            <span className="flex items-center gap-1 text-[10px] font-medium text-slate-500 uppercase tracking-wider">
              <TrendingUp className="h-3 w-3 text-emerald-500" /> Full {title} %
            </span>
            <p className="mt-0.5 text-sm font-bold text-emerald-600 truncate">
              {formatPct(achievedPct, 1)} of total
            </p>
          </div>
        </div>

        {/* Visual Progress Bar with Pacing Marker */}
        <div className="mt-2.5">
          <div className="flex items-center justify-between text-[10px] text-slate-400">
            <span>Overall Progress: <strong className="text-slate-700">{formatPct(achievedPct, 1)}</strong></span>
            <span>Elapsed Time: <strong className="text-slate-700">{timeElapsedPct.toFixed(0)}%</strong></span>
          </div>
          <div className="relative mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
            {/* Progress Fill */}
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${progressBarWidth}%`,
                background: `linear-gradient(90deg, ${color}, #10b981)`,
              }}
            />
            {/* Time Elapsed Marker Line */}
            {timeElapsedPct > 0 && timeElapsedPct < 100 ? (
              <span
                className="absolute top-0 bottom-0 w-0.5 bg-slate-400 shadow-sm"
                style={{ left: `${timeElapsedPct}%` }}
                title={`Elapsed Time: ${timeElapsedPct.toFixed(0)}%`}
              />
            ) : null}
          </div>
        </div>
      </div>

      {/* 3. Run Rate Projections Box */}
      <div className="mt-3 border-t border-slate-100 pt-2.5">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span className="flex items-center gap-1 font-semibold text-slate-700">
            <Zap className="h-3.5 w-3.5 text-amber-500" /> Run Rate Projection
          </span>
          <span className="text-[11px] text-slate-400">
            {remainingDays} days left
          </span>
        </div>

        <div className="mt-1.5 grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-2">
            <span className="text-[10px] text-slate-400 uppercase tracking-wide">Daily Run Rate</span>
            <p className="mt-0.5 text-xs font-bold text-slate-800 truncate">
              {formatVal(dailyRunRate, metricType)} / day
            </p>
          </div>
          <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-2">
            <span className="text-[10px] text-slate-400 uppercase tracking-wide">Projected Period-End</span>
            <p className="mt-0.5 text-xs font-bold text-slate-800 truncate">
              {formatVal(projectedTotal, metricType)}
            </p>
          </div>
        </div>

        {/* Achieved Rate as per Run Rate Highlight */}
        <div className="mt-2 flex items-center justify-between rounded-xl bg-gradient-to-r from-slate-50 to-slate-100/80 px-3 py-1.5">
          <div>
            <p className="text-[10px] font-medium text-slate-500">Run Rate Achieved Rate</p>
            <p className="text-[11px] text-slate-400">Projected Total vs Budget</p>
          </div>
          <div className="flex items-baseline gap-1">
            <span
              className={`text-sm font-extrabold tabular-nums ${
                isAhead ? 'text-emerald-600' : isOnTrack ? 'text-sky-600' : 'text-amber-600'
              }`}
            >
              {formatPct(runRateAchievedPct, 1)}
            </span>
            <ArrowUpRight
              className={`h-3.5 w-3.5 self-center ${
                isAhead ? 'text-emerald-500' : isOnTrack ? 'text-sky-500' : 'text-amber-500'
              }`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BiBudgetTargetCard;
